import { put, call, takeLatest, select } from 'redux-saga/effects';
import * as actions from './actions';
import { Endpoints } from 'services/api';
import errorHandler from 'common/errorHandler';

const getTenant = (state) => state.userLogin.tenant;

// API functions

function* getPipelines(api, tenantId, datasetId) {
    let url = `${Endpoints.datasets}/api/v1/pipelines`;
    if (datasetId) {
        url += `?datasetId=${datasetId}`;
    }
    let result = yield call([api, api.get], url, { tenantid: tenantId });
    return result.data;
}

function* getPipelineById(api, tenantId, pipelineId) {
    let result = yield call([api, api.get], `${Endpoints.datasets}/api/v1/pipelines/${pipelineId}`, { tenantid: tenantId });
    return result.data;
}

function* createPipeline(api, tenantId, pipelineData) {
    let result = yield call([api, api.post], `${Endpoints.datasets}/api/v1/pipelines`, pipelineData, { tenantid: tenantId });
    return result.data;
}

function* updatePipeline(api, tenantId, pipelineId, pipelineData) {
    let result = yield call([api, api.put], `${Endpoints.datasets}/api/v1/pipelines/${pipelineId}`, pipelineData, { tenantid: tenantId });
    return result.data;
}

function* deletePipeline(api, tenantId, pipelineId) {
    yield call([api, api.delete], `${Endpoints.datasets}/api/v1/pipelines/${pipelineId}`, null, { tenantid: tenantId });
}

function* runPipeline(api, tenantId, pipelineId) {
    let result = yield call([api, api.post], `${Endpoints.datasets}/api/v1/pipelines/${pipelineId}/run`, {}, { tenantid: tenantId });
    return result.data;
}

function* dryRunPipeline(api, tenantId, pipelineId) {
    let result = yield call([api, api.post], `${Endpoints.datasets}/api/v1/pipelines/${pipelineId}/dry-run`, {}, { tenantid: tenantId });
    return result.data;
}

function* dryRunPipelineInline(api, tenantId, body) {
    let result = yield call([api, api.post], `${Endpoints.datasets}/api/v1/pipelines/inline/dry-run`, body, { tenantid: tenantId });
    return result.data;
}

// Request handlers

export function* getPipelinesRequest(api, { datasetId }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getPipelines, api, tenant.tenantId, datasetId);
        yield put(actions.getPipelinesSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "pipelines");
        yield put(actions.getPipelinesFailure(errorObject));
    }
}

export function* getPipelineByIdRequest(api, { pipelineId }) {
    try {
        let tenant = yield select(getTenant);
        let pipeline = yield call(getPipelineById, api, tenant.tenantId, pipelineId);
        yield put(actions.getPipelineByIdSuccess(pipeline));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "pipeline");
        yield put(actions.getPipelineByIdFailure(errorObject));
    }
}

export function* createPipelineRequest(api, { pipelineData }) {
    try {
        let tenant = yield select(getTenant);
        let pipeline = yield call(createPipeline, api, tenant.tenantId, pipelineData);
        yield put(actions.createPipelineSuccess(pipeline));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "pipeline");
        yield put(actions.createPipelineFailure(errorObject));
    }
}

export function* updatePipelineRequest(api, { pipelineId, pipelineData }) {
    try {
        let tenant = yield select(getTenant);
        let pipeline = yield call(updatePipeline, api, tenant.tenantId, pipelineId, pipelineData);
        yield put(actions.updatePipelineSuccess(pipeline));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "pipeline");
        yield put(actions.updatePipelineFailure(errorObject));
    }
}

export function* deletePipelineRequest(api, { pipelineId }) {
    try {
        let tenant = yield select(getTenant);
        yield call(deletePipeline, api, tenant.tenantId, pipelineId);
        yield put(actions.deletePipelineSuccess());
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "pipeline");
        yield put(actions.deletePipelineFailure(errorObject));
    }
}

export function* runPipelineRequest(api, { pipelineId }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(runPipeline, api, tenant.tenantId, pipelineId);
        yield put(actions.runPipelineSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Run", "pipeline");
        yield put(actions.runPipelineFailure(errorObject));
    }
}

export function* dryRunPipelineRequest(api, { pipelineId, datasetId, steps }) {
    try {
        let tenant = yield select(getTenant);
        let result;
        if (pipelineId) {
            result = yield call(dryRunPipeline, api, tenant.tenantId, pipelineId);
        } else {
            result = yield call(dryRunPipelineInline, api, tenant.tenantId, { datasetId, steps });
        }
        yield put(actions.dryRunPipelineSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Preview", "pipeline");
        yield put(actions.dryRunPipelineFailure(errorObject));
    }
}

// Watch functions

export function* watchGetPipelinesRequest(api, { params }) {
    yield call(getPipelinesRequest, api, params);
}

export function* watchGetPipelineByIdRequest(api, { params }) {
    yield call(getPipelineByIdRequest, api, params);
}

export function* watchCreatePipelineRequest(api, { params }) {
    yield call(createPipelineRequest, api, params);
}

export function* watchUpdatePipelineRequest(api, { params }) {
    yield call(updatePipelineRequest, api, params);
}

export function* watchDeletePipelineRequest(api, { params }) {
    yield call(deletePipelineRequest, api, params);
}

export function* watchRunPipelineRequest(api, { params }) {
    yield call(runPipelineRequest, api, params);
}

export function* watchDryRunPipelineRequest(api, { params }) {
    yield call(dryRunPipelineRequest, api, params);
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_PIPELINES_REQUEST, watchGetPipelinesRequest, api);
    yield takeLatest(actions.GET_PIPELINE_BY_ID_REQUEST, watchGetPipelineByIdRequest, api);
    yield takeLatest(actions.CREATE_PIPELINE_REQUEST, watchCreatePipelineRequest, api);
    yield takeLatest(actions.UPDATE_PIPELINE_REQUEST, watchUpdatePipelineRequest, api);
    yield takeLatest(actions.DELETE_PIPELINE_REQUEST, watchDeletePipelineRequest, api);
    yield takeLatest(actions.RUN_PIPELINE_REQUEST, watchRunPipelineRequest, api);
    yield takeLatest(actions.DRY_RUN_PIPELINE_REQUEST, watchDryRunPipelineRequest, api);
}
